// C:\ShikshaPathERP_27_01\ShikshaPathERP\client\src\admin\Library\Return\index.js

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import {
  Alert,
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import Swal from "sweetalert2";

export default function BookReturn() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const isInvalidSession =
    !clientId ||
    !collegeId ||
    clientId === "null" ||
    collegeId === "null" ||
    isNaN(Number(clientId)) ||
    isNaN(Number(collegeId));

  const [idError, setIdError] = useState("");
  const [userId, setUserId] = useState("");
  const [issuedBooks, setIssuedBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [fineDetails, setFineDetails] = useState({
    late_days: 0,
    fine_amount: 0,
    fine_reason: "",
    condition: "NORMAL",
    remarks: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [userDetails, setUserDetails] = useState(null);

  useEffect(() => {
    if (!clientId || !collegeId || clientId === "null" || collegeId === "null") {
      setIdError("Missing or invalid client/college ID. Please log in again or contact admin.");
    } else {
      setIdError("");
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    const swalStyle = document.createElement("style");
    swalStyle.innerHTML = `.swal2-z-topmost { z-index: 9999 !important; } .swal2-container { z-index: 9999 !important; }`;
    swalStyle.setAttribute("data-swal-zindex", "true");
    if (!document.head.querySelector("[data-swal-zindex]")) {
      document.head.appendChild(swalStyle);
    }
    return () => {
      if (swalStyle.parentNode) swalStyle.parentNode.removeChild(swalStyle);
    };
  }, []);

  const fetchIssuedBooks = async () => {
    if (!userId || userId.trim() === "") {
      setIssuedBooks([]);
      setUserDetails(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/library/return/${clientId}/${collegeId}/user/${encodeURIComponent(
          userId
        )}`
      );

      const data = response.data?.data;

      if (response.data?.success) {
        const books = data?.books || [];
        const userDetails = data?.user_details || null;

        setIssuedBooks(books);
        setUserDetails(userDetails);

        // No user found
        if (!userDetails) {
          setSnackbar({
            open: true,
            message: "No student or staff found with this Roll Number / Staff Code",
            severity: "warning",
          });
        }
        // User found but no books issued
        else if (books.length === 0) {
          setSnackbar({
            open: true,
            message: "No issued books found for this user",
            severity: "warning",
          });
        }
      } else {
        setIssuedBooks([]);
        setUserDetails(null);

        setSnackbar({
          open: true,
          message: response.data?.message || "No issued books found for this user",
          severity: "warning",
        });
      }
    } catch (err) {
      setIssuedBooks([]);
      setUserDetails(null);

      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to fetch issued books",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUserIdBlur = () => {
    if (userId && userId.trim()) {
      fetchIssuedBooks();
    }
  };

  const handleReturnClick = (book) => {
    setSelectedBook(book);
    calculateFine(book);
    setOpenReturnDialog(true);
  };

  const calculateFine = (book) => {
    const today = new Date();
    const dueDate = new Date(book.due_date);
    const late_days = Math.max(0, Math.floor((today - dueDate) / (1000 * 60 * 60 * 24)));

    let fine_amount = 0;
    let fine_reason = "";

    if (late_days > 0) {
      const fine_per_day = parseFloat(book.fine_per_day) || 0;
      fine_amount = late_days * fine_per_day;
      fine_reason = "LATE_RETURN";
    }

    setFineDetails({
      late_days,
      fine_amount,
      fine_reason,
      condition: "NORMAL",
      remarks: "",
    });
  };

  const handleConditionChange = (event) => {
    const condition = event.target.value;
    let fine_amount = fineDetails.fine_amount;
    let fine_reason = fineDetails.fine_reason;

    if (condition === "DAMAGED") {
      // Calculate damaged fine (partial cost - 50% of book price)
      fine_amount = (parseFloat(selectedBook?.price) || 0) * 0.5;
      fine_reason = "DAMAGED";
    } else if (condition === "LOST") {
      // Calculate lost fine (book_price × cost_in_times)
      const cost_in_times = parseFloat(selectedBook?.cost_in_times) || 2;
      fine_amount = (parseFloat(selectedBook?.price) || 0) * cost_in_times;
      fine_reason = "LOST";
    } else if (condition === "NORMAL") {
      // Keep original late fine
      const today = new Date();
      const dueDate = new Date(selectedBook?.due_date);
      const late_days = Math.max(0, Math.floor((today - dueDate) / (1000 * 60 * 60 * 24)));
      fine_amount = late_days * (parseFloat(selectedBook?.fine_per_day) || 0);
      fine_reason = late_days > 0 ? "LATE_RETURN" : "";
    }

    setFineDetails({
      ...fineDetails,
      condition,
      fine_amount,
      fine_reason,
    });
  };

  const handleSubmitReturn = async () => {
    try {
      const payload = {
        issue_detail_id: selectedBook.issue_detail_id,
        condition: fineDetails.condition,
        remarks: fineDetails.remarks,
        fine_amount: fineDetails.fine_amount,
        fine_reason: fineDetails.fine_reason,
        received_by: user?.user_id || null,
        created_by: user?.user_email || "system",
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/library/return/${clientId}/${collegeId}/process-return`,
        payload
      );

      if (response.data.success) {
        await Swal.fire({
          title: "Success!",
          text: `Book returned successfully. Fine amount: ₹${fineDetails.fine_amount.toFixed(2)}`,
          icon: "success",
          confirmButtonText: "OK",
        });

        setOpenReturnDialog(false);
        setSelectedBook(null);
        fetchIssuedBooks(); // Refresh the list
      }
    } catch (err) {
      await Swal.fire({
        title: "Error!",
        text: err?.response?.data?.message || "Failed to process return",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/library/return/${clientId}/${collegeId}/returns`
      );
      const items = Array.isArray(response.data.data) ? response.data.data : [];

      if (!items.length) {
        alert("No return data to export.");
        return;
      }

      const headers = [
        "return_id",
        "User ID",
        "Book Title",
        "ISBN",
        "Return Date",
        "Fine Amount",
        "Condition",
      ];
      const csvRows = [headers.join(",")];

      items.forEach((row) => {
        csvRows.push(
          [
            row.return_id || "",
            `"${row.user_id || ""}"`,
            `"${row.book_title || ""}"`,
            `"${row.isbn || ""}"`,
            `"${row.return_date ? new Date(row.return_date).toLocaleDateString() : ""}"`,
            row.fine_amount || 0,
            `"${row.condition || "NORMAL"}"`,
          ].join(",")
        );
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "returns.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download return data as CSV.");
    }
  };

  if (idError || isInvalidSession) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          {idError || "Session expired or invalid. Please log in again or contact admin."}
        </Alert>
      </Box>
    );
  }

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 70,
      sortable: false,
      renderCell: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "accession_no",
      headerName: "Accession No",
      width: 130,
    },
    {
      field: "book_title",
      headerName: "Book Title",
      width: 250,
    },
    {
      field: "isbn",
      headerName: "ISBN",
      width: 140,
    },
    {
      field: "issue_date",
      headerName: "Issue Date",
      width: 120,
      valueGetter: (params) =>
        params.row.issue_date ? new Date(params.row.issue_date).toLocaleDateString() : "-",
    },
    {
      field: "due_date",
      headerName: "Due Date",
      width: 120,
      valueGetter: (params) =>
        params.row.due_date ? new Date(params.row.due_date).toLocaleDateString() : "-",
    },
    {
      field: "late_days_display",
      headerName: "Late Days",
      width: 100,
      valueGetter: (params) => {
        const today = new Date();
        const dueDate = new Date(params.row.due_date);

        const late_days = Math.max(0, Math.floor((today - dueDate) / (1000 * 60 * 60 * 24)));

        return late_days;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="inherit"
          size="medium"
          onClick={() => handleReturnClick(params.row)}
          sx={{
            backgroundColor: "#fff",
            color: "#000",
            boxShadow: 1,
          }}
        >
          Return
        </Button>
      ),
    },
  ];

  const rows = issuedBooks.map((book, index) => ({
    id: book.issue_detail_id,
    ...book,
  }));

  return (
    <Box p={1}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            RETURN LIBRARY BOOK
          </Typography>
        </Toolbar>

        <Box p={1}>
          {/* User ID Input Section */}

          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <TextField
                label="Roll Number / Staff Code"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter user ID"
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 46,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "1.1rem",
                    fontWeight: 700,
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "1rem",
                    fontWeight: 600,
                  },
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.85rem",
                    fontWeight: 600,
                  },
                }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={fetchIssuedBooks}
                disabled={!userId || loading}
                sx={{
                  flex: 1,
                  height: 46,
                  width: 20,
                  color: "#fff",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    color: "#fff",
                  },
                }}
              >
                Get Issued Books
              </Button>
            </Grid>
            {userDetails && (
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    User Information:
                  </Typography>
                  <Typography variant="body1">
                    <strong>Name:</strong> {userDetails.name || "N/A"} |<strong> Type:</strong>{" "}
                    {userDetails.user_type || "N/A"}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>

          {/* Issued Books Grid */}
          {issuedBooks.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Issued Books List
              </Typography>
              <Box height={560} ml={2} mr={2}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  loading={loading}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  pageSizeOptions={[10, 20, 50]}
                  disableRowSelectionOnClick
                  getRowId={(row) => row.id}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Return Dialog */}
      <Dialog
        open={openReturnDialog}
        onClose={() => setOpenReturnDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Process Book Return</DialogTitle>
        <DialogContent>
          {selectedBook && (
            <Stack spacing={3}>
              {/* Book Information */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Book Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Book Title:
                      </Typography>
                      <Typography variant="body1">{selectedBook.book_title}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Accession No:
                      </Typography>
                      <Typography variant="body1">{selectedBook.accession_no}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        ISBN:
                      </Typography>
                      <Typography variant="body1">{selectedBook.isbn}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Book Price:
                      </Typography>
                      <Typography variant="body1">
                        ₹{parseFloat(selectedBook.price || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Issue Date:
                      </Typography>
                      <Typography variant="body1">
                        {new Date(selectedBook.issue_date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Due Date:
                      </Typography>
                      <Typography variant="body1">
                        {new Date(selectedBook.due_date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Fine Details */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Fine Calculation
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Late Days:
                      </Typography>
                      <Typography
                        variant="h6"
                        color={fineDetails.late_days > 0 ? "error.main" : "success.main"}
                      >
                        {fineDetails.late_days} days
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Fine Amount:
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        ₹{fineDetails.fine_amount.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Status:
                      </Typography>
                      <Chip
                        label={fineDetails.fine_amount > 0 ? "UNPAID" : "NO FINE"}
                        color={fineDetails.fine_amount > 0 ? "error" : "success"}
                        size="medium"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Condition Selection */}
              <FormControl component="fieldset">
                <FormLabel component="legend">Book Condition</FormLabel>
                <RadioGroup row value={fineDetails.condition} onChange={handleConditionChange}>
                  <FormControlLabel value="NORMAL" control={<Radio />} label="Normal" />
                  <FormControlLabel value="DAMAGED" control={<Radio />} label="Damaged" />
                  <FormControlLabel value="LOST" control={<Radio />} label="Lost" />
                </RadioGroup>
              </FormControl>

              {/* Remarks */}
              <TextField
                fullWidth
                label="Remarks (Optional)"
                multiline
                rows={2}
                value={fineDetails.remarks}
                onChange={(e) => setFineDetails({ ...fineDetails, remarks: e.target.value })}
                placeholder="Any additional remarks about the return..."
              />

              {/* Fine Breakdown for Lost/Damaged */}
              {(fineDetails.condition === "DAMAGED" || fineDetails.condition === "LOST") && (
                <Alert severity="info">
                  {fineDetails.condition === "DAMAGED" &&
                    `Damaged book fine: 50% of book price = ₹${fineDetails.fine_amount.toFixed(2)}`}
                  {fineDetails.condition === "LOST" &&
                    `Lost book fine: Book price × ${
                      selectedBook.cost_in_times || 2
                    } = ₹${fineDetails.fine_amount.toFixed(2)}`}
                </Alert>
              )}

              {fineDetails.late_days > 0 && fineDetails.condition === "NORMAL" && (
                <Alert severity="warning">
                  Late return fine: {fineDetails.late_days} days × ₹
                  {parseFloat(selectedBook.fine_per_day || 0).toFixed(2)} = ₹
                  {fineDetails.fine_amount.toFixed(2)}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReturnDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitReturn} variant="contained" color="primary">
            Confirm Return
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
